import { useState } from 'react';
import { formatJalaliDate } from '@/lib/format';
import { formatNumber, formatPercent, persianDigits } from '@/features/reports/lib/format';
import { persianDisplayText } from '@/lib/persianUi';
import {
  useSuperAdminAdmins,
  useUpdateAdmin,
  useUpdateAdminVerification,
  useDeleteAdmin,
} from '../../hooks/useSuperAdminAdmins';
import { superAdminApi } from '../../api/superAdminApi';
import AdminCreateModal from './AdminCreateModal';

export default function AdminManagementTable() {
  const [page, setPage] = useState(1);
  const size = 10;
  const [modalState, setModalState] = useState(null);
  const [actionError, setActionError] = useState('');
  const [bannerUploadingId, setBannerUploadingId] = useState(null);

  const { data, isLoading, error } = useSuperAdminAdmins({ page, size });
  const updateAdmin = useUpdateAdmin();
  const updateVerification = useUpdateAdminVerification();
  const deleteAdmin = useDeleteAdmin();

  const totalPages = data ? Math.max(1, Math.ceil(data.total / size)) : 1;

  const toggleActive = async (admin) => {
    setActionError('');
    try {
      await updateAdmin.mutateAsync({ adminId: admin.id, adminData: { is_active: !admin.is_active } });
    } catch (err) {
      console.error(err);
      setActionError('به‌روزرسانی وضعیت با خطا مواجه شد.');
    }
  };

  const toggleVerified = async (admin) => {
    setActionError('');
    try {
      await updateVerification.mutateAsync({ adminId: admin.id, isVerified: !admin.is_verified });
    } catch (err) {
      console.error(err);
      setActionError('به‌روزرسانی تأیید با خطا مواجه شد.');
    }
  };

  const handleDelete = async (admin) => {
    if (!window.confirm(`حذف ادمین ${persianDisplayText(admin.first_name, 'بدون نام')} ${persianDisplayText(admin.last_name, '')}؟ این عمل غیرقابل بازگشت است.`)) return;
    setActionError('');
    try {
      await deleteAdmin.mutateAsync(admin.id);
    } catch (err) {
      console.error(err);
      setActionError('حذف ادمین با خطا مواجه شد.');
    }
  };

  const handleBannerUpload = async (admin, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setActionError('');
    setBannerUploadingId(admin.id);
    try {
      await superAdminApi.uploadAdminBanner(admin.id, file);
    } catch (err) {
      console.error(err);
      setActionError('بارگذاری بنر با خطا مواجه شد.');
    } finally {
      setBannerUploadingId(null);
      event.target.value = '';
    }
  };

  if (isLoading) return <div className="dashboard-loading">در حال بارگذاری ادمین‌ها...</div>;
  if (error) return <div className="admin-table-error">خطا در بارگذاری لیست ادمین‌ها.</div>;

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">مدیریت ادمین‌ها</h1>
          <p className="admin-page-subtitle">
            مشاهده و مدیریت ادمین‌های ایران‌چار‌سوق
          </p>
        </div>

        <button
          onClick={() => setModalState('create')}
          className="admin-refresh-btn"
        >
          <span>+ ادمین جدید</span>
        </button>
      </div>

      {actionError && (
        <div className="admin-table-error">
          {actionError}
        </div>
      )}

      <div className="card admin-table-card">
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>نام</th>
                <th>موبایل</th>
                <th>ایمیل</th>
                <th>وضعیت</th>
                <th>تأیید شده</th>
                <th>نرخ کمیسیون</th>
                <th>تاریخ ایجاد</th>
                <th style={{ textAlign: 'center' }}>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items ?? []).length === 0 ? (
                <tr>
                  <td colSpan="8" className="admin-table-empty">
                    هیچ ادمین ایران‌چار‌سوقی وجود ندارد.
                    <button
                      onClick={() => setModalState('create')}
                      className="admin-refresh-btn"
                      style={{ margin: '16px auto 0' }}
                    >
                      <span>ایجاد ادمین</span>
                    </button>
                  </td>
                </tr>
              ) : (
                data.items.map((admin) => (
                  <tr key={admin.id}>
                    <td>
                      <p className="admin-cell-primary">
                        {persianDisplayText(admin.first_name, 'بدون نام')} {persianDisplayText(admin.last_name, '')}
                      </p>
                    </td>
                    <td className="admin-cell-secondary">{persianDigits(admin.mobile_phone) || '—'}</td>
                    <td className="admin-cell-secondary">{admin.email ?? '—'}</td>
                    <td>
                <button
                  onClick={() => toggleActive(admin)}
                  disabled={updateAdmin.isPending}
                  className={`status-badge-sm ${admin.is_active ? 'status-delivered-badge' : 'status-cancelled-badge'}`}
                >
                  {admin.is_active ? 'فعال' : 'غیرفعال'}
                </button>
                    </td>
                    <td>
                <button
                  onClick={() => toggleVerified(admin)}
                  disabled={updateVerification.isPending}
                  className={`status-badge-sm ${admin.is_verified ? 'status-processing-badge' : 'status-pending-badge'}`}
                >
                  {admin.is_verified ? 'تأیید شده' : 'تأیید نشده'}
                </button>
                    </td>
                    <td className="admin-cell-primary">{admin.commission_rate == null ? '—' : formatPercent(admin.commission_rate)}</td>
                    <td className="admin-cell-secondary">
                      {admin.created_at ? formatJalaliDate(admin.created_at) : '—'}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        onClick={() => setModalState(admin)}
                        className="btn-sm btn-sm-success"
                      >
                        ویرایش
                      </button>
                      <button
                        onClick={() => handleDelete(admin)}
                        className="btn-sm btn-sm-danger"
                      >
                        حذف
                      </button>
                      <label className="admin-banner-upload-label">
                        {bannerUploadingId === admin.id ? 'در حال بارگذاری...' : 'بارگذاری بنر'}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          onChange={(event) => handleBannerUpload(admin, event)}
                          disabled={bannerUploadingId === admin.id}
                        />
                      </label>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="admin-pagination">
          <span>صفحه {formatNumber(page)} از {formatNumber(totalPages)}</span>
          <div className="admin-pagination-btns">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              قبلی
            </button>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              بعدی
            </button>
          </div>
        </div>
      </div>

      {modalState && (
        <AdminCreateModal
          mode={modalState === 'create' ? 'create' : 'edit'}
          admin={modalState === 'create' ? null : modalState}
          onClose={() => setModalState(null)}
        />
      )}
    </div>
  );
}